/**
 * Every recipe tonight, on demand.
 *
 * The route strip on the leading docket answers "what next" for the ticket that
 * is about to expire. It cannot answer it for the three behind it, and it
 * cannot answer it at all on a phone, where the rail lies down and the strip
 * collapses. This is the surface that does: every dish the kitchen can order,
 * each with its full crate-to-plate routes, plus a legend naming the pictograms
 * the dockets use.
 *
 * Non-modal, deliberately. The game is played by two people talking while a
 * clock runs; a panel that steals your keys mid-service is worse than one that
 * covers a corner. So: the round clock keeps running, Space still interacts
 * with whatever you face, and any movement key closes the panel *and* moves the
 * chef in the same press (`flow/App.tsx`). Nothing here traps a player.
 *
 * Nothing here is new data either. `routesFor` reads what `/api/config` already
 * sent, and every icon goes through the same `RouteIcon` the docket strip does,
 * so the panel cannot teach a kitchen the game is not running.
 *
 * Discrete by construction: whether the panel is open changes a handful of
 * times a round, so it goes through React like the rest of the HUD's discrete
 * model. There is no animation frame in this file.
 */

import type { ContainerKind, DishSummary, StationType } from '@overcooked/shared'
import { BonusChip } from './BonusChip'
import type { Catalog } from './catalog'
import { IconCanvas } from './IconCanvas'
import { drawContainer, drawPlate, drawStationBadge } from './icons'
import { RouteIcon, stationName } from './RouteIcon'
import { routeKey, routesFor } from './route'

/**
 * The panel's state, owned by the page rather than by the HUD.
 *
 * The key that toggles it is bound beside the movement keys in `flow/App.tsx`,
 * because that is the one listener allowed to swallow a key press, and a second
 * listener racing it for `r` is how a control stops working. The HUD is handed
 * the result.
 */
export interface RecipesControl {
  open: boolean
  /**
   * The dishes this kitchen can order. Empty means "the whole catalogue" — the
   * replay page, which has a catalog but no live map.
   */
  pool: string[]
  toggle(): void
  close(): void
}

/**
 * Whether a player wants the panel up from the first whistle.
 *
 * Set by the tutorial: somebody who read it through gets the recipes open and
 * somebody who pressed "skip — I know this" does not, because they said so. A
 * preference, not a record — it is `localStorage`, so a new browser profile is
 * a new player, which is the honest reading of it.
 *
 * Wrapped because `localStorage` throws outright in a locked-down profile, and
 * a panel preference is not worth a blank page.
 */
export const RECIPES_AUTO_OPEN_KEY = 'overcooked.recipes.autoOpen'

export function prefersRecipesOpen(): boolean {
  try {
    return window.localStorage.getItem(RECIPES_AUTO_OPEN_KEY) === '1'
  } catch {
    return false
  }
}

interface RecipePanelProps {
  catalog: Catalog
  dishes: DishSummary[]
  recipes: RecipesControl
}

export function RecipePanel({ catalog, dishes, recipes }: RecipePanelProps) {
  const pool = recipes.pool
  const shown = pool.length > 0 ? dishes.filter((dish) => pool.includes(dish.id)) : dishes

  return (
    // `aria-live="off"` because the HUD as a whole is a polite live region —
    // it announces the flash line — and a panel of two dozen icons appearing
    // inside one would be read out end to end over a running round. It is
    // opened deliberately, so it is read deliberately.
    <section className="recipe-panel" id="recipe-panel" aria-label="Recipes" aria-live="off">
      <header className="recipe-head">
        <h2 className="recipe-title">Recipes</h2>
        <p className="recipe-hint">
          Press <kbd>R</kbd> or <kbd>Esc</kbd> to close. The clock keeps running.
        </p>
        <button type="button" className="recipe-close" onClick={recipes.close}>
          Close
        </button>
      </header>

      <div className="recipe-list">
        {shown.length === 0 && <p className="recipe-empty">The menu hasn't loaded yet.</p>}
        {shown.map((dish) => (
          <article className="recipe-card" key={dish.id}>
            <div className="recipe-card-head">
              <IconCanvas
                className="recipe-plate"
                size={52}
                signature={`recipe-plate-${dish.contents.join('+')}`}
                title={`${dish.label}, plated`}
                draw={(ctx, size) =>
                  drawPlate(
                    ctx,
                    { id: dish.id, clean: true, contents: dish.contents },
                    size / 2,
                    size / 2,
                    size * 0.44,
                    catalog,
                  )
                }
              />
              <h3 className="recipe-name">
                {dish.label}
                <BonusChip bonus={dish.bonus} />
              </h3>
            </div>

            {/* One row per distinct route. Three identically-routed vegetables
                are one instruction, and printing it three times teaches nothing
                it does not teach once — `routesFor` deduplicates, and the count
                lives on the docket's own badge row. */}
            <ol className="recipe-routes">
              {routesFor(dish.requires).map((route) => (
                <li className="recipe-route" key={routeKey(route)}>
                  <ol className="ticket-route-row" aria-label="Steps, in order">
                    {route.map((step, i) => (
                      <li className="ticket-route-step" key={`${routeKey([step])}-${i}`}>
                        {i > 0 && <span className="ticket-route-chevron" aria-hidden="true" />}
                        <RouteIcon step={step} catalog={catalog} size={32} />
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ol>
          </article>
        ))}
      </div>

      {/* The legend. The dockets are pictograms and a pictogram nobody has been
          introduced to is a puzzle — this is the one place in the round where
          the drawing and its name sit side by side. Derived from what the shown
          dishes actually need, so a station no map uses is never explained. */}
      <RecipeLegend catalog={catalog} dishes={shown} />
    </section>
  )
}

function RecipeLegend({ catalog, dishes }: { catalog: Catalog; dishes: DishSummary[] }) {
  const stations: StationType[] = []
  const containers: ContainerKind[] = []
  for (const dish of dishes) {
    for (const station of dish.requires.stations) {
      if (!stations.includes(station)) stations.push(station)
    }
    for (const container of dish.requires.containers) {
      if (!containers.includes(container)) containers.push(container)
    }
  }
  if (stations.length === 0 && containers.length === 0) return null

  return (
    <div className="recipe-legend">
      <span className="ticket-route-cap">Key</span>
      <ul className="recipe-legend-list">
        {stations.map((station) => (
          <li className="recipe-legend-item" key={`station-${station}`}>
            <IconCanvas
              className="ticket-route-icon"
              size={28}
              signature={`legend-station-${station}`}
              title={stationName(station)}
              draw={(ctx, s) => drawStationBadge(ctx, station, s / 2, s / 2, s * 0.34)}
            />
            <span>{stationName(station)}</span>
          </li>
        ))}
        {containers.map((kind) => (
          <li className="recipe-legend-item" key={`container-${kind}`}>
            <IconCanvas
              className="ticket-route-icon"
              size={28}
              signature={`legend-container-${kind}`}
              title={catalog.containerLabel(kind)}
              draw={(ctx, s) =>
                drawContainer(
                  ctx,
                  { id: kind, kind, contents: [], cookState: 'empty', phaseStartedAtTick: null, phaseEndsAtTick: null },
                  s / 2,
                  s / 2,
                  s * 0.38,
                  catalog,
                )
              }
            />
            <span>{catalog.containerLabel(kind)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
